/**
 * Inquiry form option data — kept centralized so we can evolve tier
 * scales as the business calibrates without hunting through JSX.
 *
 * Values are slugs (safe for DB queries / analytics filters).
 * Labels are the display strings.
 */

export const PROJECT_TYPES = [
    { value: "single-room", label: "Single Room / Focused Project" },
    { value: "renovation", label: "Renovation / Retrofit" },
    { value: "new-construction", label: "New Construction — Whole Home" },
    { value: "estate", label: "Estate / Compound" },
    { value: "yacht", label: "Yacht / Marine" },
    { value: "commercial", label: "Commercial / Office" },
    { value: "consulting", label: "Consulting Only" },
];

const DEFAULT_BUDGET_TIERS = [
    { value: "under-50k", label: "Under $50K" },
    { value: "50k-150k", label: "$50K – $150K" },
    { value: "150k-500k", label: "$150K – $500K" },
    { value: "500k-1m", label: "$500K – $1M" },
    { value: "1m-plus", label: "$1M+" },
    { value: "not-sure", label: "Not sure yet" },
];

const BUDGET_TIERS_BY_PROJECT = {
    "single-room": [
        { value: "under-10k", label: "Under $10K" },
        { value: "10k-25k", label: "$10K – $25K" },
        { value: "25k-50k", label: "$25K – $50K" },
        { value: "50k-100k", label: "$50K – $100K" },
        { value: "100k-plus", label: "$100K+" },
        { value: "not-sure", label: "Not sure yet" },
    ],
    renovation: [
        { value: "25k-75k", label: "$25K – $75K" },
        { value: "75k-150k", label: "$75K – $150K" },
        { value: "150k-350k", label: "$150K – $350K" },
        { value: "350k-750k", label: "$350K – $750K" },
        { value: "750k-plus", label: "$750K+" },
        { value: "not-sure", label: "Not sure yet" },
    ],
    "new-construction": [
        { value: "75k-200k", label: "$75K – $200K" },
        { value: "200k-500k", label: "$200K – $500K" },
        { value: "500k-1m", label: "$500K – $1M" },
        { value: "1m-2.5m", label: "$1M – $2.5M" },
        { value: "2.5m-plus", label: "$2.5M+" },
        { value: "not-sure", label: "Not sure yet" },
    ],
    estate: [
        { value: "250k-750k", label: "$250K – $750K" },
        { value: "750k-2m", label: "$750K – $2M" },
        { value: "2m-5m", label: "$2M – $5M" },
        { value: "5m-10m", label: "$5M – $10M" },
        { value: "10m-25m", label: "$10M – $25M" },
        { value: "25m-plus", label: "$25M+" },
        { value: "not-sure", label: "Not sure yet" },
    ],
    yacht: [
        { value: "50k-150k", label: "$50K – $150K" },
        { value: "150k-500k", label: "$150K – $500K" },
        { value: "500k-1.5m", label: "$500K – $1.5M" },
        { value: "1.5m-5m", label: "$1.5M – $5M" },
        { value: "5m-plus", label: "$5M+" },
        { value: "not-sure", label: "Not sure yet" },
    ],
    commercial: [
        { value: "25k-75k", label: "$25K – $75K" },
        { value: "75k-250k", label: "$75K – $250K" },
        { value: "250k-750k", label: "$250K – $750K" },
        { value: "750k-plus", label: "$750K+" },
        { value: "not-sure", label: "Not sure yet" },
    ],
    consulting: [
        { value: "hourly-tbd", label: "Hourly engagement" },
        { value: "under-10k", label: "Under $10K" },
        { value: "10k-25k", label: "$10K – $25K" },
        { value: "25k-50k", label: "$25K – $50K" },
        { value: "50k-plus", label: "$50K+" },
    ],
};

/**
 * Returns the appropriate budget tiers for a given project-type slug.
 * When no project type is picked yet (empty string), returns a sensible
 * default so the dropdown never renders empty.
 */
export function getBudgetTiers(projectType) {
    return BUDGET_TIERS_BY_PROJECT[projectType] || DEFAULT_BUDGET_TIERS;
}

/**
 * Whether a given budget slug is valid for a given project-type slug.
 * Used by the form hook to auto-clear stale selections.
 */
export function isBudgetValidFor(projectType, budgetValue) {
    if (!budgetValue) return true;
    return getBudgetTiers(projectType).some((t) => t.value === budgetValue);
}

/**
 * Canonical services offered by ACT — reused across the site.
 * The single source of truth is `SERVICES` in ./content.js. This form-facing
 * projection uses shorter labels tuned for the multi-select pill selector.
 */
export const INQUIRY_SERVICES = [
    { value: "home-automation", label: "Home Automation & Control" },
    { value: "lighting-shades", label: "Lighting & Motorized Shades" },
    { value: "home-theater-audio", label: "Home Theater & Distributed Audio" },
    { value: "security-surveillance", label: "Security & Surveillance" },
    { value: "networking-wifi", label: "Networking & Wi-Fi" },
    { value: "commercial", label: "Commercial & Estate Integration" },
];

/**
 * Curated country list (ISO-3166 alpha-2 codes) — the ~26 markets the studio
 * actively serves. Anything else falls under "Other" which swaps the state
 * and city dropdowns for free-text inputs.
 */
export const INQUIRY_COUNTRIES_ISO = [
    "US", // United States (default)
    "CA", // Canada
    "MX", // Mexico
    "GB", // United Kingdom
    "IE", // Ireland
    "FR", // France
    "IT", // Italy
    "ES", // Spain
    "PT", // Portugal
    "CH", // Switzerland
    "MC", // Monaco
    "GR", // Greece
    "DE", // Germany
    "AT", // Austria
    "NL", // Netherlands
    "AE", // United Arab Emirates
    "SA", // Saudi Arabia
    "BS", // Bahamas
    "KY", // Cayman Islands
    "BM", // Bermuda
    "TC", // Turks and Caicos
    "AU", // Australia
    "NZ", // New Zealand
    "SG", // Singapore
    "HK", // Hong Kong
];

/**
 * Sentinel value used when the user selects a country outside the curated
 * list. The location subform swaps to free-text inputs.
 */
export const OTHER_COUNTRY_VALUE = "OTHER";
